'use client';

import React, { useState } from 'react';
import { Copy, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { toast } from 'sonner';

interface DemoAccount {
  role: string;
  email: string;
  password: string;
}

interface DemoCredentialsProps {
  accounts: DemoAccount[];
  onSelect: (email: string, password: string) => void;
}

// Demo credentials are only shown in non-production environments
const isDevelopment = process.env.NODE_ENV !== 'production';

export default function DemoCredentials({ accounts, onSelect }: DemoCredentialsProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Hide entirely in production
  if (!isDevelopment) return null;

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied`);
    });
  };

  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <button
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-muted hover:bg-muted/80 transition-colors"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          <Zap size={13} className="text-gold" />
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Demo Accounts
          </span>
          <span className="text-2xs text-muted-foreground/60 font-normal">(Development only)</span>
        </div>
        {isExpanded ? (
          <ChevronUp size={14} className="text-muted-foreground" />
        ) : (
          <ChevronDown size={14} className="text-muted-foreground" />
        )}
      </button>

      {isExpanded && (
        <div className="divide-y divide-border fade-in">
          {accounts.map((account) => (
            <div
              key={`demo-${account.role}`}
              className="flex items-center justify-between px-4 py-3 bg-card hover:bg-muted/30 transition-colors group"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-2xs font-bold text-primary uppercase tracking-wide">{account.role}</span>
                </div>
                <p className="text-xs text-foreground font-medium truncate">{account.email}</p>
                <p className="text-2xs text-muted-foreground truncate">{account.password}</p>
              </div>
              <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
                <button
                  onClick={() => handleCopy(account.email, 'Email')}
                  className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  title="Copy email"
                  aria-label="Copy email address"
                >
                  <Copy size={12} />
                </button>
                <button
                  onClick={() => onSelect(account.email, account.password)}
                  className="px-3 py-1.5 rounded-lg bg-primary-light text-primary text-2xs font-bold hover:bg-primary hover:text-white transition-all btn-press"
                  aria-label={`Use ${account.role} credentials`}
                >
                  Use
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}